import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  FaPython, FaNode, FaReact, FaAws, FaDocker, FaGitAlt,
  FaDatabase, FaCode, FaCloud, FaRobot
} from 'react-icons/fa';
import { 
  SiJavascript, SiMongodb, SiPostgresql, SiJenkins,
  SiTensorflow, SiPytorch, SiBootstrap, SiDjango
} from 'react-icons/si';
import { useLanguage } from '../utils/LanguageContext';
import './AllComponents.css';

const Skills = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.2
  });

  const { t } = useLanguage();

  const skillCategories = [
    {
      title: t('skills.categories.programming'),
      icon: <FaCode />,
      skills: [
        { name: "Java", icon: <FaCode />, level: 90 },
        { name: "JavaScript", icon: <SiJavascript />, level: 88 },
        { name: "Python", icon: <FaPython />, level: 85 },
        { name: "C++", icon: <FaCode />, level: 75 }
      ]
    },
    {
      title: t('skills.categories.frontend'),
      icon: <FaReact />,
      skills: [
        { name: "React.js", icon: <FaReact />, level: 85 },
        { name: "HTML/CSS", icon: <FaCode />, level: 90 },
        { name: "Bootstrap", icon: <SiBootstrap />, level: 82 },
        { name: "Django", icon: <SiDjango />, level: 78 }
      ]
    },
    {
      title: t('skills.categories.backend'),
      icon: <FaCloud />,
      skills: [
        { name: "Node.js", icon: <FaNode />, level: 88 },
        { name: "REST API", icon: <FaCode />, level: 92 },
        { name: "Jenkins", icon: <SiJenkins />, level: 80 },
        { name: "Docker", icon: <FaDocker />, level: 82 }
      ]
    },
    {
      title: t('skills.categories.aiCloud'),
      icon: <FaRobot />,
      skills: [
        { name: "PyTorch", icon: <SiPytorch />, level: 75 },
        { name: "TensorFlow", icon: <SiTensorflow />, level: 72 },
        { name: "AWS", icon: <FaAws />, level: 80 },
        { name: "MongoDB", icon: <SiMongodb />, level: 85 }
      ]
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };

  return (
    <section id="skills" className="section skills-section">
      <div className="container">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <motion.div variants={itemVariants} className="section-header text-center">
            <h2 className="gradient-text">{t('skills.technicalSkills')}</h2>
            <p className="section-subtitle">
              {t('skills.skillsSubtitle')}
            </p>
          </motion.div>

          <div className="skills-grid">
            {skillCategories.map((category, index) => (
              <motion.div
                key={index}
                className="skill-category card"
                variants={itemVariants}
                whileHover={{ scale: 1.02 }}
              >
                <div className="category-header">
                  <div className="category-icon">
                    {category.icon}
                  </div>
                  <h3>{category.title}</h3>
                </div>
                
                <div className="skills-list">
                  {category.skills.map((skill, skillIndex) => (
                    <div key={skillIndex} className="skill-item">
                      <div className="skill-info">
                        <div className="skill-name">
                          <span className="skill-icon">{skill.icon}</span>
                          {skill.name}
                        </div>
                        <span className="skill-percentage">{skill.level}%</span>
                      </div>
                      <div className="skill-bar">
                        <motion.div 
                          className="skill-progress"
                          initial={{ width: 0 }}
                          animate={inView ? { width: `${skill.level}%` } : { width: 0 }}
                          transition={{ 
                            duration: 1.5, 
                            delay: index * 0.2 + skillIndex * 0.1,
                            ease: "easeOut"
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Skills; 